import { Button, TYPE } from '@hub/common/Button/Button';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useLocaleSelectionContext } from '@hub/hooks/useLocaleSelectionContext';
import { Footer as DefaultFooter } from '@hub/layout/Footer/Footer';
import { isCorrectFooterObject } from '@hub/libs/isCorrectFooterObject';
import type { Footer, ImageLink, PlaceholderDiv, TextLink } from '@hub/models/api/page-content.types';
import { useEffect, useState } from 'react';

function isImageLink(link: TextLink | ImageLink): link is ImageLink {
  return (link as ImageLink)?.image !== null;
}

function isTextLink(link: TextLink | ImageLink): link is TextLink {
  return !('image' in link) || link?.image === null;
}

const CONTACT_ICON_MAP = {
  'phone-new': IconID.PHONE,
  'mail-new': IconID.MAIL,
  whatsapp: IconID.WHATSAPP,
  zalo: IconID.ZALO,
  line: IconID.LINE,
};

type KnownIcons = keyof typeof CONTACT_ICON_MAP;

function isKnownIcon(icon?: string): icon is KnownIcons {
  return !!icon && Object.keys(CONTACT_ICON_MAP).includes(icon);
}

const Section = ({
  headline,
  children,
  className,
  testid,
}: {
  headline?: string | null;
  children: React.ReactNode;
  className?: string;
  testid?: string;
}) => (
  <section className={className} data-testid={testid}>
    {headline ? <h4 className="text-base font-bold mt-8 mb-5 leading-[27px]">{headline}</h4> : null}
    {children}
  </section>
);

const LinkList = ({ links }: { links: { href: string; text?: string; icon?: string }[] }) =>
  links && !links.length ? null : (
    <ul>
      {links.map(link => (
        <li key={link.text}>
          <a className="text-sm leading-loose inline-flex items-center gap-4" href={link.href}>
            {isKnownIcon(link.icon) && <Icon id={CONTACT_ICON_MAP[link.icon]} size={6} color={IconColor.DARK_GRAY} />}
            {link.text}
          </a>
        </li>
      ))}
    </ul>
  );

const TextArray = ({ rows }: { rows: string[] }) =>
  !rows.length ? null : (
    <div className="mb-5" data-testid="footer-contact-text-section">
      {rows.map(row => (
        <div className="text-sm" key={row}>
          {row}
        </div>
      ))}
    </div>
  );

const awardImageClass = 'inline align-top mr-5 mb-5';
const AwardsList = ({ awards }: { awards: (ImageLink | PlaceholderDiv)[] }) =>
  !awards.length ? null : (
    <div data-testid="award-images-div" className="max-w-[270px]">
      {awards.map(item => {
        if ('id' in item && item.id) {
          return <div key={item.id} id={item.id} className={awardImageClass} data-testid="dynamic-award" />;
        }
        if ('image' in item) {
          return item.href ? (
            <a key={item.text} href={item.href}>
              <img
                src={item.image}
                alt={item.text}
                width={item.width}
                height={item.height}
                className={awardImageClass}
              />
            </a>
          ) : (
            <img
              key={item.text}
              src={item.image}
              alt={item.text}
              width={item.width}
              height={item.height}
              className={awardImageClass}
            />
          );
        }
        return null;
      })}
    </div>
  );

const PaymentList = ({
  links,
  className = '',
  size = { width: 80, height: 40 },
}: {
  links: (TextLink | ImageLink)[];
  className?: string;
  size?: { width: number; height: number };
}) => {
  /* We assume here that the whole list should be rendered 'plain' if one of its elements has type='plain' set.
     This is not a perfect implementation, but given that the whole footer implementation is deprecated, it is
     justifiable to keep the effort low. The footer will soon be replaced by the new CMS footer. */

  const isPlainList =
    links.filter(link => link && isTextLink(link)).find(link => (link as TextLink).type === 'plain') !== undefined;

  return (
    <ul className={`${isPlainList ? '' : 'flex flex-wrap gap-4'} ${className}`}>
      {links.map(link => (
        <li key={link.text}>
          {/* TextLink to be rendered as a-tag with href */}
          {isTextLink(link) && link.href && (
            <a
              className="text-sm leading-loose inline-flex items-center gap-4"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.text}
            </a>
          )}

          {/* TextLink to be rendered as text in plain style */}
          {isTextLink(link) && !link.href && link.type === 'plain' && (
            <div className="text-sm leading-loose flex-none items-center gap-4">{link.text}</div>
          )}

          {/* TextLink to be rendered as text in card style */}
          {isTextLink(link) && !link.href && link.type !== 'plain' && (
            <div
              className="border-[0.5px] border-[#ccc] rounded-lg bg-white overflow-hidden relative"
              style={{ width: size.width, height: size.height }}
            >
              <span
                style={{ textShadow: 'none' }}
                className="absolute top-[50%] translate-y-[-50%] left-0 right-0 font-bold uppercase text-igus-primary text-center text-[11px] tracking-[1px] leading-[1.1]"
              >
                {link.text}
              </span>
            </div>
          )}

          {/* ImageLink */}
          {isImageLink(link) && <img src={link.image} alt={link.text} width={size.width} height={size.height} />}
        </li>
      ))}
    </ul>
  );
};

const SocialList = ({
  links,
  size = { width: 80, height: 40 },
}: {
  links: Array<TextLink | ImageLink>;
  size?: { width: number; height: number };
}) => (
  <ul className="flex flex-wrap gap-4" data-testid="footer-subscribe-section">
    {links.map(link => (
      <li key={link.text}>
        {/* tailwind has no support for textShadow and creating an utility ourselves for 1 occassion seems too much overhead */}
        <a href={link.href} style={{ textShadow: 'none' }}>
          {isImageLink(link) ? (
            <img src={link.image} alt={link.text} width={size.width} height={size.height} />
          ) : (
            <div
              className="border-[0.5px] border-[#ccc] rounded-lg bg-white overflow-hidden relative"
              style={{ width: size.width, height: size.height }}
            >
              <span className="absolute top-[50%] translate-y-[-50%] left-0 right-0 font-bold uppercase text-igus-primary text-center text-[11px] tracking-[1px] leading-[1.1]">
                {link.text}
              </span>
            </div>
          )}
        </a>
      </li>
    ))}
  </ul>
);

export const NewFooter = ({ footerJson }: { footerJson: Footer }) => {
  // The firstRow and SecondRow is done on purpose because there should always be
  // two rows defined.
  const { toggleLocaleSelectionVisibility } = useLocaleSelectionContext();
  const [clientReady, setClientReady] = useState(false);

  useEffect(() => {
    setClientReady(true);
  }, []);

  return (
    <div>
      {isCorrectFooterObject(footerJson) ? (
        <footer className="igus-text-styles" data-testid="new-footer-component">
          <section className="bg-gray-500 py-4">
            <div className="container text-gray-50">
              <span className="text-base mr-6 font-medium">{footerJson.feedback.text}</span>
              <Button
                type={TYPE.LIGHT}
                text={footerJson.feedback.button.text}
                href={footerJson.feedback.button.href}
                className="!w-auto !min-w-[132px]"
              />
            </div>
          </section>
          <section className="bg-[#f7f7f9] text-gray-500 pb-16 pt-[30px]">
            <div className="container igus-text-styles grid gap-x-10 md:grid-cols-2 lg:grid-cols-3 auto-rows-auto">
              <Section headline={footerJson.about.headline}>
                <LinkList links={footerJson.about.links} />
              </Section>
              <Section headline={footerJson.services.headline}>
                <LinkList links={footerJson.services.links} />
              </Section>
              <Section headline={footerJson.contact.headline}>
                <LinkList links={footerJson.contact.links} />
                <TextArray rows={footerJson.contact.text} />
              </Section>
              {footerJson.payment?.firstRow && footerJson.payment?.secondRow && (
                <Section className="lg:self-end" headline={footerJson.payment.headline}>
                  <PaymentList links={footerJson.payment.firstRow} className="mb-4" />
                  <PaymentList links={footerJson.payment.secondRow} />
                </Section>
              )}
              {footerJson.payment?.links && (
                <Section className="lg:self-end" headline={footerJson.payment.headline}>
                  <PaymentList links={footerJson.payment.links} />
                </Section>
              )}
              {clientReady && footerJson.awards && footerJson.awards.links.length > 0 ? (
                <Section className="lg:self-end" headline={footerJson.awards.headline} testid="footer-award-section">
                  <AwardsList awards={footerJson.awards.links} />
                </Section>
              ) : (
                <section />
              )}
              {footerJson.subscribe?.links ? (
                <Section className="lg:mt-[-20px]" headline={footerJson.subscribe.headline}>
                  <SocialList
                    links={footerJson.subscribe.links as Array<TextLink | ImageLink>}
                    size={{ width: 30, height: 30 }}
                  />
                  <p className="text-sm mt-4 mb-8">{footerJson.subscribe.text}</p>
                  <Button
                    type={TYPE.PRIMARY}
                    text={footerJson.subscribe.button.text}
                    href={footerJson.subscribe.button.href}
                    className="w-auto min-w-[132px] rounded-[3px] px-8"
                  />
                </Section>
              ) : null}
              {footerJson.escrow ? (
                <Section className="w-[32rem] flex igus-text-styles-no-shadow-link">
                  <a
                    href={footerJson.escrow.link}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-12 mr-5 w-[10rem]"
                    data-testid="footer-escrow-image-container"
                  >
                    <img data-testid="footer-escrow-image" src={footerJson.escrow.imageSrc} alt="" />
                  </a>
                  <a
                    href={footerJson.escrow.link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-igus-primary leading-loose"
                    data-testid="footer-escrow-text"
                  >
                    <ul className="text-base font-bold mt-8 ml-5 text-sm igus-text-color-secondary">
                      {footerJson.escrow.text.map((text: string) => (
                        <li key={text} className="inline-flex">
                          {text}
                        </li>
                      ))}
                    </ul>
                  </a>
                </Section>
              ) : null}
            </div>
            <div className="container flex lg:justify-end text-sm items-center mt-24">
              <span className="mr-4 align-middle">
                <Icon id={IconID.GLOBE} size={6} />
              </span>
              <span className="mr-2 font-bold">{footerJson.language.label}</span>
              <span className="cursor-pointer" onClick={() => toggleLocaleSelectionVisibility()}>
                {footerJson.language?.value}
              </span>
              <span className="mx-2">|</span>
              <span className="mr-2 font-bold">{footerJson.country.label}</span>
              <span className="cursor-pointer" onClick={() => toggleLocaleSelectionVisibility()}>
                {footerJson.country.value}
              </span>
            </div>
            <div className="container my-8">
              <hr />
            </div>
            <div className="container text-[#bbb] text-sm">
              <ul className="inline-flex flex-wrap leading-8">
                <li className="text-[#666]">© igus, {new Date().getFullYear()}</li>
                {footerJson.boilerplate.links.map(link => (
                  <li key={link.text}>
                    <span className="mx-2">|</span>
                    <a href={'href' in link ? link.href : ''} className="!text-[#666]">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
              <div
                className="[&>p]:mb-4 [&>p]:leading-[18px] py-8"
                dangerouslySetInnerHTML={{ __html: footerJson.boilerplate.trademarks }}
              />
            </div>
          </section>
        </footer>
      ) : (
        <DefaultFooter />
      )}
    </div>
  );
};
